import type { ISODate } from '@bigint-project/core';
import { parseISODate } from './date.js';
import { fail } from './errors.js';

export const SYSCAL_PREFIX = 'syscal:' as const;
const SOURCE_KEY_RE = /^[a-z0-9][a-z0-9-]{0,63}$/;
const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const SYSCAL_ID_RE = /^syscal:([a-z0-9][a-z0-9-]{0,63}):(\d{4}):(\d{4}-\d{2}-\d{2})$/;

export interface ParsedSyscalId {
  readonly sourceKey: string;
  readonly year: number;
  readonly date: ISODate;
}

export function assertSourceKey(sourceKey: string): void {
  if (!SOURCE_KEY_RE.test(sourceKey)) {
    fail('INVALID_SOURCE_KEY', `Invalid calendar source key: ${sourceKey}`);
  }
}

function assertDateMatchesYear(date: string, year: number): asserts date is ISODate {
  const match = ISO_DATE_RE.exec(date);
  const parsed = parseISODate(date);
  if (match === null || parsed === null || Number(match[1]) !== year) {
    fail('INVALID_SYSCAL_ID', `System calendar date ${date} does not belong to ${year}`);
  }
}

export function makeSyscalId(sourceKey: string, year: number, date: ISODate): string {
  assertSourceKey(sourceKey);
  if (!Number.isInteger(year) || year < 1 || year > 9999) {
    fail('INVALID_SYSCAL_ID', `Invalid system calendar year: ${year}`);
  }
  assertDateMatchesYear(date, year);
  return `${SYSCAL_PREFIX}${sourceKey}:${year}:${date}`;
}

export function parseSyscalId(id: string): ParsedSyscalId {
  const match = SYSCAL_ID_RE.exec(id);
  if (match === null) fail('INVALID_SYSCAL_ID', `Invalid system calendar exception id: ${id}`);
  const sourceKey = match[1]!;
  const year = Number(match[2]);
  const date = match[3]!;
  assertSourceKey(sourceKey);
  assertDateMatchesYear(date, year);
  return { sourceKey, year, date };
}

export function isSyscalId(id: string): boolean {
  return id.startsWith(SYSCAL_PREFIX);
}
